import { Dispatch, SetStateAction, useCallback } from "react";
import { toast } from "sonner";

import {
  copyToClipboard,
  exportAgentWorkflow,
} from "../utils/importExportUtils";

interface UseHandleExportArgs {
  agentId: string;
  setIsExporting: Dispatch<SetStateAction<boolean>>;
  setImportError: Dispatch<SetStateAction<string | null>>;
  setIsExportDropdownOpen?: Dispatch<SetStateAction<boolean>> | null;
}

/**
 * useHandleExport returns a memoised handler that exports an agent workflow,
 * updates UI state, and optionally toggles an export dropdown when a setter
 * is provided.
 */
export function useHandleExport({
  agentId,
  setIsExporting,
  setImportError,
  setIsExportDropdownOpen,
}: UseHandleExportArgs) {
  return useCallback(async () => {
    if (!agentId) {
      return;
    }

    setIsExporting(true);
    setImportError(null);

    try {
      const result = await exportAgentWorkflow(agentId);

      if (!result) {
        toast.error("Failed to export agent workflow", {
          description:
            "Please try again. If the issue persists, contact support.",
        });
        return;
      }

      const copied = await copyToClipboard(result.jsonString);

      if (copied) {
        toast.success("Agent workflow JSON copied to clipboard");
      } else {
        toast.error("Clipboard is not available in this browser");
      }

      setIsExportDropdownOpen?.(false);
    } catch (_error) {
      toast.error("Failed to export agent workflow", {
        description:
          "Please try again. If the issue persists, contact support.",
      });
    } finally {
      setIsExporting(false);
    }
  }, [agentId, setImportError, setIsExportDropdownOpen, setIsExporting]);
}
